import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
// Import the flag hooks from their own submodule rather than the `common/hooks`
// barrel: the smart-apply components that consume this hook mock that exact
// submodule path in their tests, and a barrel import would bypass those mocks.
import { usePostHogFeatureFlagPayload } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import {
  GrantAdvisoryBookingUrlPayload,
  POSTHOG_FEATURE_FLAGS
} from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { logger } from '@percent/cause-dashboard/common/utility/logger'
import {
  BookingTracking,
  GrantAdvisoryEntryPoint,
  buildBookingUrl,
  resolveBookingBaseUrl
} from './grantAdvisoryBooking.utils'

/*
  Resolves the booking URL for an entry point from the CP_GRANT_ADVISORY_CALENDLY
  flag payload, with UTM attribution applied.

  The payload is the only source of these URLs - nothing is hardcoded - so sales can
  repoint the booking links in PostHog without a deploy. Returns `undefined` when the
  payload carries no usable URL for this entry point, which callers must treat as
  "booking unavailable" rather than falling back to a stale link.
*/
export const useGrantAdvisoryBookingUrl = (
  entryPoint: GrantAdvisoryEntryPoint,
  tracking?: BookingTracking
): string | undefined => {
  const payload = usePostHogFeatureFlagPayload<GrantAdvisoryBookingUrlPayload>(
    POSTHOG_FEATURE_FLAGS.CP_GRANT_ADVISORY_CALENDLY
  )
  // Re-sample the page's utm_* params on navigation. Without this the URL would
  // freeze the params from wherever the consuming component first mounted, and the
  // sidebar promo lives in the side menu, which survives every route change.
  const { search } = useLocation()

  const bookingUrl = useMemo(
    () => buildBookingUrl(resolveBookingBaseUrl(payload, entryPoint), tracking, search),
    [payload, entryPoint, tracking, search]
  )

  // The payload is the single point of failure for this flow, and an unusable value
  // silently hides the CTA. Surface it so a bad edit is diagnosable rather than
  // showing up weeks later as a drop in booked calls.
  useEffect(() => {
    if (payload && !bookingUrl) {
      logger(new Error(`Grant advisory booking URL unusable for entry point "${entryPoint}"`))
    }
  }, [payload, bookingUrl, entryPoint])

  return bookingUrl
}
